import React from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import { SyncProblem, Smartphone } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { processMobileData } from './utils';
import { DASHBOARD_COLORS } from './styles';
import DataRow from './shared/DataRow';
import BatteryIndicator from './shared/BatteryIndicator';
import { getAppStatus } from './utils';
import { IssueIcon } from '@/resources/icons/IssueIcon';
import { FirmwareLookupTableOnAzure } from '../../../../../../generated-client/models';
import { UserStatusPingViewModel } from '@/models/User/UserStatusPingViewModel';

interface MobileDeviceSectionProps {
  userStatus: UserStatusPingViewModel | null;
  firmwareLookup: FirmwareLookupTableOnAzure | null;
}

const MobileDeviceSection = ({
  userStatus,
  firmwareLookup,
}: MobileDeviceSectionProps) => {
  const { t } = useTranslation();

  const deviceInfo = userStatus?.dto.device_info;

  const hasLocationPermissionIssue =
    userStatus?.hasDeviceLocationPermissionIssue();

  const hasNotificationPermissionIssue =
    userStatus?.hasDeviceNotificationPermissionIssue();

  const mobileData = processMobileData(deviceInfo);

  const { appOutdated } = getAppStatus(deviceInfo, firmwareLookup);

  return (
    <Paper
      //@ts-ignore
      variant="contained"
      sx={{
        p: 2,
        flex: {
          xs: 1,
          md: 2,
        },
        backgroundColor: DASHBOARD_COLORS.background,
        borderRadius: 2,
      }}
    >
      {/* Mobile Header with Image and Specs Side by Side */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Mobile Specs - Right Side */}
        <Box sx={{ flex: 1 }}>
          {/* Title and Battery */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box>
              <Typography
                variant="body1"
                fontSize=".95rem"
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                }}
              >
                {mobileData.manufacturer}
              </Typography>

              <Typography variant="caption" sx={{ color: COLORS.GREY }}>
                {mobileData.model}
              </Typography>
            </Box>
            <Smartphone
              sx={{
                fontSize: 45,
                color: COLORS.GREY,
                backgroundColor: DASHBOARD_COLORS.background,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BatteryIndicator percentage={mobileData.batteryLevel} />
            </Box>
          </Box>

          {/* Permissions Title */}
          <Typography
            variant="body1"
            fontSize=".95rem"
            sx={{ fontWeight: 'bold', mb: 1 }}
          >
            {t(StringKeys.PERMISSIONS)}
          </Typography>

          {/* Permissions List - Vertical */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <DataRow label={t(StringKeys.LOCATION)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasLocationPermissionIssue && <IssueIcon />}
                <Typography variant="body1" fontSize=".95rem">
                  {t(mobileData.locationPermissions)}
                </Typography>
              </Box>
            </DataRow>

            <DataRow label={t(StringKeys.NOTIFICATIONS)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasNotificationPermissionIssue && <IssueIcon />}
                <Typography variant="body1" fontSize=".95rem">
                  {t(mobileData.notificationsPermissions)}
                </Typography>
              </Box>
            </DataRow>

            {/* DIVIDER LINE */}
            <Divider
              sx={{
                my: 1,
                backgroundColor: DASHBOARD_COLORS.mutedText,
                opacity: 0.3,
                height: '1px',
                transform: 'scaleY(1)',
              }}
            />

            <DataRow label={t(StringKeys.APP_VERSION)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {appOutdated && (
                  <SyncProblem sx={{ fontSize: 16, color: COLORS.RED }} />
                )}
                <Typography variant="body1">{mobileData.appVersion}</Typography>
              </Box>
            </DataRow>
            <DataRow
              label={t(StringKeys.OPERATING_SYSTEM) || 'Operating System'}
            >
              <Typography variant="body1" fontSize=".95rem">
                {mobileData.osDisplay}
              </Typography>
            </DataRow>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default MobileDeviceSection;
